import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SpinnerComponent, toggleContentFadeIn, toggleContentFadeOut, TooltipDirective } from '@kaseya-design-components';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@kaseya-shared';
import { NotificationsFacade } from '@layout/notifications/facades/notifications.facade';
import { NotificationComponent } from '@layout/notifications/components/notification/notification.component';

@Component({
  selector: 'itc-notifications-toggle-panel',
  templateUrl: 'notifications-toggle-panel.component.html',
  styleUrl: 'notifications-toggle-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, NotificationComponent, SpinnerComponent, TooltipDirective],
  animations: [toggleContentFadeIn, toggleContentFadeOut],
})
export class NotificationsTogglePanelComponent implements OnDestroy {
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly notificationsFacade = inject(NotificationsFacade);

  readonly hasAccessToNotificationsModify = this.permissionsFacade.hasPermission(Permission.NotificationsModify);

  readonly isCollapsed = computed(() => !this.notificationsFacade.areNotificationsVisible());
  readonly notifications = computed(() => this.notificationsFacade.notifications() ?? []);
  readonly notificationsLoading = this.notificationsFacade.loading;
  readonly unreadNotificationsCount = computed(() => this.notificationsFacade.unreadNotificationsCount() ?? 0);

  constructor() {
    this.notificationsFacade.loadNotifications();
    this.collapsePanelIfNoNotifications();
  }

  togglePanel(): void {
    if (!this.notifications().length && this.unreadNotificationsCount() === 0) {
      return;
    }
    this.notificationsFacade.setNotificationsVisibility(this.isCollapsed());
  }

  markAllNotificationsAsViewed(): void {
    if (!this.hasAccessToNotificationsModify()) {
      return;
    }

    this.notificationsFacade.markAllNotificationsAsViewed();
  }

  ngOnDestroy(): void {
    this.notificationsFacade.setNotificationsVisibility(false);
  }

  private collapsePanelIfNoNotifications(): void {
    effect(() => {
      if (this.notificationsFacade.notifications()?.length) {
        return;
      }

      this.notificationsFacade.setNotificationsVisibility(false);
    });
  }
}
